import { useNavigate } from 'react-router-dom';

import { AppIcon } from '@backrs_ui/common-ui';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { ReactComponent as LinkedinIcon } from '@backrs_ui/grow-ui/src/assets/images/linkedin-blue.svg';
import { ProfileBanner } from '@backrs_ui/grow-ui/src/components/ProfileBanner/ProfileBanner';
import { UserProfileResponse } from '@backrs_ui/grow-ui/src/redux/profile/profile';
import { BACKRS_APP_URL } from '@backrs_ui/grow-ui/src/settings';

import { Avatar, Stack, Theme, SxProps, Typography, Box, Button } from '@mui/material';

import { useStudentDetails } from '../hooks/useStudentDetails';

const PROFILE_HEADER_STRINGS = appStrings.modules.profile.public.header;

type PublicProfileHeaderProps = Readonly<{
  publicProfile?: UserProfileResponse;
  isOwnProfile: boolean;
  onScheduleConsultation?: () => void;
  isCoachingEnabled?: boolean;
  /** Replaces the details under the name when set, even when empty. */
  programLabel?: string;
}>;

export const PublicProfileHeader = ({
  publicProfile,
  isOwnProfile,
  onScheduleConsultation,
  isCoachingEnabled,
  programLabel,
}: PublicProfileHeaderProps) => {
  const navigate = useNavigate();

  const { isStudent, isInHighSchool, graduationYear, gradeLabel, major, firstValue } =
    useStudentDetails(publicProfile);

  const { first_name, last_name, profile_image, city, state } =
    publicProfile?.profile?.publicProfile ?? {};
  const isCoach = publicProfile?.profile?.coaching && isCoachingEnabled;
  const linkedinUrl = publicProfile?.profile?.linkedInData?.linkedIn_url?.[0]?.value;

  const formatCityState = () => {
    const location = [city, state].filter(Boolean);

    return location.length > 0 ? location.join(', ') : undefined;
  };

  const handleBackClick = () => {
    if (isOwnProfile) {
      navigate(APP_ROUTE.PROFILE);
    } else {
      window.open(
        `${BACKRS_APP_URL}/profile/${publicProfile?.profile?.publicProfile.id}`,
        '_blank',
      );
    }
  };

  const doesStudentHaveEnrollmentOrGraduationYear: boolean = isStudent || !!graduationYear;

  const doesFirstColumnHaveContent: boolean = isStudent
    ? !!doesStudentHaveEnrollmentOrGraduationYear ||
      !!firstValue('academicsHighSchoolCurrentEnrollment') ||
      !!firstValue('academicsPostSecondaryCurrentEnrollment')
    : !!publicProfile?.profile?.careerOccupation || !!publicProfile?.profile?.careerEmployer?.[0];

  return (
    <Stack sx={containerSx}>
      <Box sx={bannerLayerSx}>
        <ProfileBanner
          id="public-profile-banner"
          bannerImageUrl={publicProfile?.profile?.publicProfile?.profile_banner_image}
          seed={publicProfile?.profile?.publicProfile?.id ?? ''}
          sx={bannerSx}
        />
      </Box>

      <Stack sx={contentContainerSx}>
        <Avatar src={profile_image} sx={avatarSx} />

        <Box sx={nameInfoContainerSx}>
          <Box sx={nameActionsContainerSx}>
            <Typography sx={nameSx}>
              {first_name} {last_name}
            </Typography>

            <Box sx={actionsContainerSx}>
              {isCoach && (
                <Button
                  id="profile-linkedin-button"
                  variant="contained"
                  size="md"
                  hierarchy="Secondary gray"
                  disabled={!linkedinUrl}
                  onClick={() => {
                    window.open(linkedinUrl, '_blank');
                  }}
                >
                  <LinkedinIcon width="20px" height="20px" />
                </Button>
              )}

              <Button
                id="profile-edit-button"
                variant="contained"
                size="md"
                hierarchy="Primary"
                disabled={isCoach && !isOwnProfile && !onScheduleConsultation}
                onClick={
                  isCoach && !isOwnProfile && onScheduleConsultation
                    ? onScheduleConsultation
                    : handleBackClick
                }
              >
                {isOwnProfile
                  ? PROFILE_HEADER_STRINGS.edit
                  : isCoach
                    ? PROFILE_HEADER_STRINGS.scheduleConsultation
                    : PROFILE_HEADER_STRINGS.back}
              </Button>
            </Box>
          </Box>

          {programLabel !== undefined ? (
            <Typography
              id="profile-program-label"
              variant="text-md-regular"
              color={({ backrs }) => backrs.colors['Colors/Text/text-tertiary']}
            >
              {programLabel}
            </Typography>
          ) : (
            <Box sx={textItemsContainerSx}>
              {(doesStudentHaveEnrollmentOrGraduationYear || !isStudent) && (
                <Box sx={textItemsBoxSx(doesFirstColumnHaveContent || false)}>
                  {/* ACADEMIC GRADE (if student) */}
                  {gradeLabel && isStudent && (
                    <Typography variant="text-sm-medium" sx={textItemSx}>
                      <AppIcon
                        iconName="Clock"
                        diameter="16px"
                        color="Colors/Border/border-brand"
                      />
                      {gradeLabel}
                    </Typography>
                  )}

                  {/* CAREER OCCUPATION (if professional) */}
                  {publicProfile?.profile?.careerOccupation && !isStudent && (
                    <Typography variant="text-sm-medium" sx={textItemSx}>
                      <AppIcon
                        iconName="Clock"
                        diameter="16px"
                        color="Colors/Border/border-brand"
                      />
                      {publicProfile.profile.careerOccupation}
                    </Typography>
                  )}

                  {/* CURRENT ENROLLMENT (if high school student) */}
                  {firstValue('academicsHighSchoolCurrentEnrollment') &&
                    isStudent &&
                    isInHighSchool && (
                      <Typography variant="text-sm-medium" sx={textItemSx}>
                        <AppIcon
                          iconName="Bank"
                          diameter="16px"
                          color="Colors/Border/border-brand"
                        />
                        {firstValue('academicsHighSchoolCurrentEnrollment')}
                      </Typography>
                    )}

                  {/* CURRENT ENROLLMENT (if post secondary student) */}
                  {firstValue('academicsPostSecondaryCurrentEnrollment') &&
                    isStudent &&
                    !isInHighSchool && (
                      <Typography variant="text-sm-medium" sx={textItemSx}>
                        <AppIcon
                          iconName="Bank"
                          diameter="16px"
                          color="Colors/Border/border-brand"
                        />
                        {firstValue('academicsPostSecondaryCurrentEnrollment')}
                      </Typography>
                    )}

                  {/* CAREER EMPLOYER (if professional) */}
                  {publicProfile?.profile?.careerEmployer?.[0] && !isStudent && (
                    <Typography variant="text-sm-medium" sx={textItemSx}>
                      <AppIcon
                        iconName="Certificate01"
                        diameter="16px"
                        color="Colors/Border/border-brand"
                      />
                      {publicProfile.profile.careerEmployer[0]}
                    </Typography>
                  )}
                </Box>
              )}

              <Box sx={textItemsBoxSx(true)}>
                {/* LOCATION */}
                {formatCityState() && (
                  <Typography variant="text-sm-medium" sx={textItemSx}>
                    <AppIcon
                      iconName="MarkerPin01"
                      diameter="16px"
                      color="Colors/Border/border-brand"
                    />
                    {formatCityState()}
                  </Typography>
                )}

                {/* MAJOR (if student) */}
                {major && isStudent && (
                  <Typography variant="text-sm-medium" sx={textItemSx}>
                    <AppIcon
                      iconName="Certificate01"
                      diameter="16px"
                      color="Colors/Border/border-brand"
                    />
                    {major}
                  </Typography>
                )}

                {/* ALMA MATER (if professional) */}
                {firstValue('academicsAlmaMaters') && !isStudent && (
                  <Typography variant="text-sm-medium" sx={textItemSx}>
                    <AppIcon iconName="Bank" diameter="16px" color="Colors/Border/border-brand" />
                    {firstValue('academicsAlmaMaters')}
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </Box>
      </Stack>
    </Stack>
  );
};

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  width: '100%',
  display: 'flex',
  flexDirection: { xs: 'column', sm: 'row' },
  alignItems: { xs: 'flex-start', sm: 'center' },
  paddingBottom: backrs.spacing['spacing-4xl'],
  position: 'relative',
  paddingTop: {
    xs: 'calc(120px / 1.25)',
    md: 'calc(216px / 1.25)',
    lg: 'calc(160px / 1.25)',
  },
  gap: { xs: backrs.spacing['spacing-lg'], sm: backrs.spacing['spacing-2xl'] },
  flexWrap: { xs: 'wrap' },
});

const contentContainerSx: SxProps<Theme> = ({ backrs }) => ({
  width: '100%',
  display: 'flex',
  flexDirection: { xs: 'column', sm: 'row' },
  alignItems: { xs: 'flex-start', sm: 'center' },
  position: 'relative',
  margin: '0 auto',
  maxWidth: '1216px',

  paddingBottom: {
    xs: '0 !important',
    sm: backrs.spacing['spacing-4xl'],
  },
  paddingTop: 0,
  paddingLeft: {
    xs: backrs.spacing['spacing-xl'],
    sm: backrs.spacing['spacing-4xl'],
  },
  paddingRight: {
    xs: backrs.spacing['spacing-xl'],
    sm: backrs.spacing['spacing-4xl'],
  },
  gap: { xs: backrs.spacing['spacing-lg'], sm: backrs.spacing['spacing-2xl'] },
  flexWrap: { xs: 'wrap' },
});

const bannerLayerSx: SxProps<Theme> = ({ backrs }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  zIndex: 0,
  display: 'flex',
  justifyContent: 'center',
  paddingLeft: { xs: backrs.spacing['spacing-xl'], sm: backrs.spacing['spacing-4xl'] },
  paddingRight: { xs: backrs.spacing['spacing-xl'], sm: backrs.spacing['spacing-4xl'] },
});

const bannerSx: SxProps<Theme> = {
  maxWidth: '1216px',
  height: {
    xs: '120px',
    md: '160px',
  },
};

const nameSx: SxProps<Theme> = ({ typography, breakpoints }) => ({
  [breakpoints.down('md')]: {
    ...typography['display-xs-semibold'],
  },

  [breakpoints.up('md')]: {
    ...typography['display-sm-semibold'],
  },
});

const nameInfoContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  zIndex: 1,
  alignItems: 'flex-start',
  alignSelf: { xs: 'flex-start', sm: 'flex-end' },
  mb: backrs.spacing['spacing-md'],
  width: {
    xs: '100%',
    sm: `calc(100% - ${backrs.spacing['spacing-11xl']} - ${backrs.spacing['spacing-3xl']})`,
  },
  flexWrap: 'wrap',
});

const avatarSx: SxProps<Theme> = ({ backrs }) => ({
  width: {
    xs: backrs.spacing['spacing-9xl'],
    sm: backrs.spacing['spacing-11xl'],
  },
  height: {
    xs: backrs.spacing['spacing-9xl'],
    sm: backrs.spacing['spacing-11xl'],
  },
  borderRadius: '50%',
  border: `2px solid ${backrs.colors['Beige/950']}`,
  overflow: 'hidden',
  '& .MuiAvatar-img': {
    objectFit: 'cover',
    width: '100%',
    height: '100%',
  },
});

const textItemSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-md'],
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

const textItemsContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: { xs: backrs.spacing['spacing-xs'], sm: backrs.spacing['spacing-3xl'] },
  columnGap: { xs: backrs.spacing['spacing-lg'], sm: backrs.spacing['spacing-3xl'] },
  width: '100%',
  marginTop: backrs.spacing['spacing-xl'],
  flexWrap: { xs: 'wrap' },
});

const textItemsBoxSx =
  (shouldDisplay: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    display: shouldDisplay ? 'flex' : 'none',
    flexDirection: 'column',
    gap: backrs.spacing['spacing-xs'],
  });

const nameActionsContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: backrs.spacing['spacing-md'],
  width: '100%',
  flexWrap: 'wrap',
  marginTop: { sm: backrs.spacing['spacing-4xl'], md: backrs.spacing['spacing-none'] },
});

const actionsContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: backrs.spacing['spacing-lg'],
  '& .MuiButton-root': {
    boxSizing: 'border-box',
    height: '40px',
  },
  flexWrap: 'wrap',
});
